import fetch from 'isomorphic-fetch';

import config, { env } from 'config';
import reportErrorToNewRelic from 'utils/reportErrorToNewRelic';

export interface LineupCamLogEntry {
  focusPolygon: Array<{ x: number; y: number }>;
  overviewSmartCameraId: string;
  timestamp: number;
}

interface LineupCamLogApiEntry {
  focusPolygon: Array<{ x: number; y: number }>;
  overviewSmartCameraId: string;
  status: string;
  timestamp: number;
}

const STALENESS_THRESHOLD_MS = env === 'production' ? 15 * 60 * 1000 : 24 * 60 * 60 * 1000; // 15 min prod, 24h non-prod
const MIN_POLYGON_POINTS = 3;
const MIN_POLYGON_AREA = 0.01;

const isValidFocusPolygon = (polygon: Array<{ x: number; y: number }>): boolean => {
  if (polygon.length < MIN_POLYGON_POINTS) return false;
  if (!polygon.every((p) => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1)) return false;
  const left = Math.min(...polygon.map((p) => p.x));
  const top = Math.min(...polygon.map((p) => p.y));
  const right = Math.max(...polygon.map((p) => p.x));
  const bottom = Math.max(...polygon.map((p) => p.y));
  return (right - left) * (bottom - top) >= MIN_POLYGON_AREA;
};

export const fetchLineupCamLog = async (
  lineupCameraId: string,
): Promise<LineupCamLogEntry | null> => {
  const res = await fetch(
    `${config.servicesURL}/cameras/lineup-cam-log?lineupCameraId=${lineupCameraId}&limit=1`,
  );

  if (!res.ok) {
    if (res.status >= 500) {
      const error = new Error(`LineupCamLog fetch error: ${res.statusText}`) as Error & {
        statusCode: number;
      };
      error.statusCode = res.status;
      reportErrorToNewRelic(error, 'lineupCamLog', {
        statusCode: res.status,
        url: res.url,
      });
    }
    return null;
  }

  const json: LineupCamLogApiEntry[] | { data: LineupCamLogApiEntry[] } = await res.json();
  const entries = Array.isArray(json) ? json : json.data;
  const successEntry = entries?.find((entry) => entry.status === 'SUCCESS');

  if (!successEntry?.focusPolygon || !successEntry?.overviewSmartCameraId) {
    return null;
  }

  // Check staleness
  const ageMs = Date.now() - successEntry.timestamp;
  if (ageMs > STALENESS_THRESHOLD_MS) {
    reportErrorToNewRelic(
      new Error(`LineupCamLog stale: ${Math.round(ageMs / 60000)}min old`),
      'lineupCamLogStale',
      { cameraId: lineupCameraId, ageMinutes: Math.round(ageMs / 60000) },
      true,
    );
    return null;
  }

  // Validate polygon
  if (!isValidFocusPolygon(successEntry.focusPolygon)) {
    reportErrorToNewRelic(
      new Error('LineupCamLog invalid focusPolygon'),
      'lineupCamLogInvalidPolygon',
      { cameraId: lineupCameraId, polygon: JSON.stringify(successEntry.focusPolygon) },
      true,
    );
    return null;
  }

  return {
    focusPolygon: successEntry.focusPolygon,
    overviewSmartCameraId: successEntry.overviewSmartCameraId,
    timestamp: successEntry.timestamp,
  };
};
